import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button, Empty, ErrorBlock, Img, Input, Spinner } from "../components/ui.tsx";
import { api } from "../api/client.ts";
import { cn } from "@/lib/utils";

/**
 * ═══════════════════════════════════════════════════════════════
 *  页面管理 · 登录页管理
 * ═══════════════════════════════════════════════════════════════
 *
 * 老板：「后台需要一个地方可以配置这 3 个东西，页面管理下的登录页管理」——
 * 指的是网页版登录页左边那块品牌区，从上到下三样：
 * **小标题 · 大标题 · 商品图**。
 *
 * 🔴 **这几样以前是写死在组件里的**，改一个字要发一次版。而它是整个站
 *    第一眼看到的地方，运营改文案的频率远高于发版的频率。
 *
 * 🔴 **改完要作废前台那份缓存**（`["site-config"]`）。两边是两个查询 ——
 *    只存不作废的话，他保存成功、点去登录页一看还是老文案，
 *    会以为没保存上然后反复来回改。这个坑在品类和店铺货架上已经踩过两次。
 */

interface LoginPageCfg {
  eyebrow: string;
  headline: string;
  showcase: "sales" | "manual" | "off";
  showcaseProductIds: number[];
}

interface SettingRecord {
  key: string;
  value: LoginPageCfg;
  updatedAt: string | null;
}

interface ProductRow {
  id: number;
  title: string;
  imageUrls: string[];
}

/** 最多能挑几件 —— 登录页那一格是 3 列，两行正好。 */
const MAX_PICK = 6;

export function LoginPageTab() {
  const qc = useQueryClient();

  const rec = useQuery({
    queryKey: ["admin", "settings", "loginPage"],
    queryFn: () => api.get<SettingRecord>("/admin/settings/loginPage"),
  });

  const [draft, setDraft] = useState<LoginPageCfg | null>(null);
  /** ⚠️ 只在第一次拿到数据时铺进表单，别每次 refetch 都把他正在打的字盖掉。 */
  useEffect(() => {
    if (rec.data && draft === null) setDraft(rec.data.value);
  }, [rec.data, draft]);

  /** 挑商品用的候选：按销量给一批，够挑了。 */
  const products = useQuery({
    queryKey: ["admin", "settings", "loginPage", "products"],
    queryFn: () => api.get<{ items: ProductRow[] }>("/products?sort=sales&limit=48"),
    enabled: draft?.showcase === "manual",
  });

  const save = useMutation({
    mutationFn: () =>
      api.put(`/admin/settings/loginPage`, {
        value: draft,
        expectedUpdatedAt: rec.data?.updatedAt ?? null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "settings"] });
      /** 🔴 前台那份也要作废，见文件头。 */
      void qc.invalidateQueries({ queryKey: ["site-config"] });
    },
  });

  if (rec.isPending) return <Spinner compact />;
  if (rec.error) return <ErrorBlock error={rec.error} />;
  if (!draft) return <Spinner compact />;

  const picked = draft.showcaseProductIds;
  const toggle = (id: number) => {
    const next = picked.includes(id)
      ? picked.filter((x) => x !== id)
      : picked.length >= MAX_PICK
        ? picked
        : [...picked, id];
    setDraft({ ...draft, showcaseProductIds: next });
  };

  return (
    /*
      🔴 **两栏铺满，别让右边空一大片。**（老板：「右边还有很大留白，均匀分配各区域占比」）
         后台内容区有一千八百多宽，把整页封在 720 里的话右边就是一片白。
         但**输入框本身不能跟着拉到一千八**（那比窄的更难用）——
         所以是"两栏各占一半"：左边三块配置，右边预览。宽度被内容用掉了，
         输入框也还在一个正常的宽度上。

      ⚠️ 预览用 `sticky`：左边挑商品那一格能滚很长，预览跟着滚出屏幕的话，
         他改一下要往回滚才看得到效果，等于没有预览。
    */
    <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-2">
      <div className="space-y-6">
        <section className="rounded-xl border border-border bg-card p-5">
          <p className="mb-4 text-[15px] font-medium">① 小标题</p>
          <Input
            value={draft.eyebrow}
            onChange={(e) => setDraft({ ...draft, eyebrow: e.target.value })}
            placeholder="装修建材 · 一站购齐"
          />
        </section>

        <section className="rounded-xl border border-border bg-card p-5">
          <p className="text-[15px] font-medium">② 大标题</p>
          <p className="mt-1 mb-3 text-[13px] text-muted-foreground">
            换行就是换行 —— 这一句是有意折行的，交给浏览器自己折的话，屏宽一变断句就散了。
          </p>
          <textarea
            rows={2}
            value={draft.headline}
            onChange={(e) => setDraft({ ...draft, headline: e.target.value })}
            className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-[14px] leading-relaxed outline-none focus:border-brand-500"
            placeholder={"装一套房要的建材\n在这儿一次配齐"}
          />
        </section>

        <section className="rounded-xl border border-border bg-card p-5">
          <p className="text-[15px] font-medium">③ 商品图</p>
          <p className="mt-1 mb-3 text-[13px] text-muted-foreground">登录页左下那六张图。</p>

          <div className="space-y-2">
            {(
              [
                {
                  k: "sales" as const,
                  t: "按销量自动取前 6 名",
                  d: "卖得好的自动排上去，不用管。⚠️ 销量一变，图就跟着变。",
                },
                {
                  k: "manual" as const,
                  t: "我自己挑",
                  d: "挑定之后不会自己变。顺序就是显示顺序。",
                },
                { k: "off" as const, t: "不显示这一块", d: "左边只留文字。" },
              ] as const
            ).map((o) => (
              <label
                key={o.k}
                className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors",
                  draft.showcase === o.k
                    ? "border-brand-500 bg-brand-50/50"
                    : "border-border hover:bg-secondary",
                )}
              >
                <input
                  type="radio"
                  className="mt-1"
                  checked={draft.showcase === o.k}
                  onChange={() => setDraft({ ...draft, showcase: o.k })}
                />
                <span className="min-w-0">
                  <span className="block text-[14px]">{o.t}</span>
                  <span className="mt-0.5 block text-[12px] text-muted-foreground">{o.d}</span>
                </span>
              </label>
            ))}
          </div>

          {draft.showcase === "manual" && (
            <div className="mt-4">
              <p className="mb-2 text-[13px] text-muted-foreground">
                已挑 {picked.length} / {MAX_PICK} 件
                {picked.length > 0 && picked.length < 3 && (
                  /**
                   * ⚠️ 登录页那一块**少于 3 张就整块不出现**（组件里就是这么写的：
                   *    宁可不显示也不留一排半空的格子）。不说清楚的话，
                   *    他挑了两张、保存成功、去看却什么都没有。
                   */
                  <span className="ml-2 text-amber-700">
                    少于 3 张的话登录页上这一块不会出现，至少挑 3 件
                  </span>
                )}
              </p>

              {products.isPending ? (
                <Spinner compact />
              ) : products.error ? (
                <ErrorBlock error={products.error} />
              ) : (products.data?.items ?? []).length === 0 ? (
                <Empty title="还没有在架商品可挑" compact />
              ) : (
                <div className="grid max-h-[420px] grid-cols-3 gap-2 overflow-y-auto rounded-lg border border-border p-2 sm:grid-cols-4">
                  {(products.data?.items ?? []).map((p) => {
                    const at = picked.indexOf(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => toggle(p.id)}
                        className={cn(
                          "relative overflow-hidden rounded-lg border text-left transition-colors",
                          at >= 0 ? "border-brand-500" : "border-border hover:border-brand-300",
                        )}
                      >
                        <Img
                          src={p.imageUrls?.[0]}
                          alt={p.title}
                          className="aspect-[4/3] w-full object-cover"
                        />
                        {/* 挑中的标出**第几张** —— 顺序就是登录页上的顺序。 */}
                        {at >= 0 && (
                          <span className="absolute top-1 left-1 flex size-5 items-center justify-center rounded-full bg-brand-600 text-[11px] text-white">
                            {at + 1}
                          </span>
                        )}
                        <span className="block truncate px-2 py-1.5 text-[12px]">{p.title}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </section>

        {save.error && <ErrorBlock error={save.error} />}
        <div className="flex items-center gap-3">
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            保存
          </Button>
          {save.isSuccess && !save.isPending && (
            <span className="text-[13px] text-muted-foreground">已保存，去登录页就能看到</span>
          )}
        </div>
      </div>

      {/* ── 右栏：预览。所见即所得，省得他保存完再跳去登录页看一眼 ── */}
      <section className="overflow-hidden rounded-xl border border-border xl:sticky xl:top-6">
        <p className="border-b border-border bg-secondary/40 px-4 py-2 text-[13px] text-muted-foreground">
          预览（登录页左边那一块）
        </p>
        <div className="bg-gradient-to-br from-brand-600 to-brand-500 p-6">
          <p className="text-[13px] text-white/75">{draft.eyebrow}</p>
          {/*
            ⚠️ `whitespace-pre-line` 才会把 `\n` 当换行渲染 ——
               登录页那边是 `<br/>` 拆的，两边看起来要一致。
          */}
          <p className="mt-2 text-[24px] leading-tight font-medium whitespace-pre-line text-white">
            {draft.headline}
          </p>
          {draft.showcase !== "off" && (
            <div className="mt-5 grid grid-cols-3 gap-2">
              {(draft.showcase === "manual"
                ? picked
                    .map((id) => (products.data?.items ?? []).find((p) => p.id === id))
                    .filter(Boolean)
                    .map((p) => p!.imageUrls?.[0])
                : []
              )
                .filter(Boolean)
                .map((src) => (
                  <Img
                    key={src}
                    src={src}
                    alt=""
                    className="aspect-[4/3] w-full rounded-lg object-cover ring-1 ring-white/15"
                  />
                ))}
              {draft.showcase === "sales" && (
                <p className="col-span-3 rounded-lg bg-white/10 px-3 py-4 text-center text-[12px] text-white/80">
                  按销量自动取前 6 名，这里不预览具体是哪几张
                </p>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
